import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import InfoBanner from '@/components/InfoBanner/InfoBanner';
import RewardCard from '@/components/RewardCard/RewardCard';
import SwiperTabs from '@/components/SwiperTabs/SwiperTabs';
import {useExternalLinksBatch} from '@/hooks/useExternalLinks';
import {selectAvailableRewards, selectNotEarnedRewards, selectRedeemedRewards} from '@/state/selectors/loyalty';
import type {IconModel, InfoBannerModel, TabbedRewardsSectionContentModel} from '@/types/aem';
import type {UiReward} from '@/types/loyalty';

const TAB_VALUES = {
    AVAILABLE: 'available',
    NOT_EARNED: 'notEarned',
    REDEEMED: 'redeemed',
} as const;

type TabbedRewardsSectionProps = {
    content?: TabbedRewardsSectionContentModel | null;
};

type RewardListProps = {
    rewards: UiReward[];
    emptyState?: InfoBannerModel | null;
    redeemedLabel?: string | null;
    notEarnedRewardIcon?: IconModel | null;
    isEarned?: boolean;
    showImage?: boolean;
    rewardDetailExternalLink?: TabbedRewardsSectionContentModel['rewardDetailExternalLink'];
};

const RewardList = ({
    rewards,
    emptyState,
    redeemedLabel,
    notEarnedRewardIcon,
    isEarned = true,
    showImage = true,
    rewardDetailExternalLink,
}: RewardListProps) => {
    if (rewards.length === 0) {
        return (
            <div className='flex flex-1 items-center justify-center px-4'>
                <InfoBanner content={emptyState} className='mb-4' />
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-2 px-4'>
            {rewards.map((reward, index) => (
                <RewardCard
                    key={`${reward.id}-${index}`}
                    reward={reward}
                    redeemedLabel={redeemedLabel}
                    notEarnedRewardIcon={notEarnedRewardIcon}
                    isEarned={isEarned}
                    showImage={showImage}
                    rewardDetailExternalLink={rewardDetailExternalLink}
                />
            ))}
        </div>
    );
};

const TabbedRewardsSection = ({content}: TabbedRewardsSectionProps) => {
    const {
        availableTabLabel,
        notEarnedTabLabel,
        redeemedTabLabel,
        redeemedLabel,
        notEarnedRewardIcon,
        rewardDetailExternalLink,
        availableTabEmptyState,
        notEarnedTabEmptyState,
        redeemedTabEmptyState,
    } = content ?? {};

    const availableRewards = useSelector(selectAvailableRewards);
    const notEarnedRewards = useSelector(selectNotEarnedRewards);
    const redeemedRewards = useSelector(selectRedeemedRewards);

    const rewardExternalLinkItems = useMemo(
        () =>
            [...availableRewards, ...notEarnedRewards, ...redeemedRewards].map(({journeyId, id: rewardId}) => ({
                externalLink: rewardDetailExternalLink,
                params: {journeyId, rewardId},
            })),
        [availableRewards, notEarnedRewards, redeemedRewards, rewardDetailExternalLink]
    );

    // Prefetch all reward external links in one batched request to avoid many per-tab calls.
    useExternalLinksBatch({items: rewardExternalLinkItems});

    const tabs = [
        {
            value: TAB_VALUES.AVAILABLE,
            testId: `${TAB_VALUES.AVAILABLE}-tab-button`,
            label: availableTabLabel,
            children: (
                <RewardList
                    rewards={availableRewards}
                    emptyState={availableTabEmptyState}
                    rewardDetailExternalLink={rewardDetailExternalLink}
                />
            ),
        },
        {
            value: TAB_VALUES.NOT_EARNED,
            testId: `${TAB_VALUES.NOT_EARNED}-tab-button`,
            label: notEarnedTabLabel,
            children: (
                <RewardList
                    rewards={notEarnedRewards}
                    emptyState={notEarnedTabEmptyState}
                    notEarnedRewardIcon={notEarnedRewardIcon}
                    isEarned={false}
                    rewardDetailExternalLink={rewardDetailExternalLink}
                />
            ),
        },
        {
            value: TAB_VALUES.REDEEMED,
            testId: `${TAB_VALUES.REDEEMED}-tab-button`,
            label: redeemedTabLabel,
            children: (
                <RewardList
                    rewards={redeemedRewards}
                    emptyState={redeemedTabEmptyState}
                    redeemedLabel={redeemedLabel}
                    showImage={false}
                    rewardDetailExternalLink={rewardDetailExternalLink}
                />
            ),
        },
    ];

    return <SwiperTabs className='mt-4' tabs={tabs} />;
};

export default TabbedRewardsSection;
